import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "../utils/toast";
import WorkWithUs from "../components/sections/WorkWithUs";
import { useVehicles } from "../hooks/VehicleContext";
import {
  BOOKING_DRAFT_STORAGE_KEY,
  CHECKOUT_BOOKING_STORAGE_KEY,
} from "../utils/bookingStorageKeys";
import {
  resolveCheckoutBookingData,
  hasRoundTripLegData,
} from "../utils/checkoutBookingResolve";
import { CHECK_FARE_TOAST_ID } from "../utils/apiValidationErrors";
import { launchBookingPayment } from "../utils/paymentLauncher";
import {
  formatDistanceKmForDisplay,
  resolveOnwardDistanceKm,
  resolveReturnDistanceKm,
} from "../utils/bookingFormatters";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";  

/** Match DateSelectModal `getFormattedDate` when we only have API date/time strings. */
function formatScheduleLikeDatePicker(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (/AM|PM/i.test(s)) return s;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})/);
  if (m) {
    const d = new Date(
      Number(m[1], 10),
      Number(m[2], 10) - 1,
      Number(m[3], 10),
      Number(m[4], 10),
      Number(m[5], 10),
    );
    if (!Number.isNaN(d.getTime())) {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      let h = d.getHours();
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12;
      if (h === 0) h = 12;
      const hh = String(h).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${hh}:${mm} ${ampm}`;
    }
  }
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    const d = new Date(t);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    const hh = String(h).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${hh}:${mm} ${ampm}`;
  }
  return s;
}

function resolveCheckoutVehicleImage(vehicle) {
  if (!vehicle || typeof vehicle !== "object") {
    return "/assets/img/all-images/sedan-check.png";
  }

  const imageUrl = String(vehicle.image_url || vehicle.imageUrl || "").trim();
  if (imageUrl) return imageUrl;

  const rawImage = String(vehicle.image || "").trim();
  if (!rawImage) return "/assets/img/all-images/sedan-check.png";
  if (rawImage.startsWith("http://") || rawImage.startsWith("https://")) {
    return rawImage;
  }
  if (rawImage.startsWith("/assets/")) {
    return `${process.env.REACT_APP_APP_DOMAIN}${rawImage}`;
  }
  return `${process.env.REACT_APP_APP_DOMAIN}/storage/${rawImage.replace(/^\/+/, "")}`;
}

const RIDE_AIRPORT = 1;
const RIDE_CITYTOUR = 2;
const RIDE_OUTSTATION = 3;
const ACTIVE_CHECKOUT_BOOKING_KEY = "orbi_active_checkout_booking_v1";
const RESUME_INQUIRY_AFTER_AUTH_KEY = "orbi_resume_inquiry_after_auth_v1";
const RESUME_PAYMENT_AFTER_AUTH_KEY = "orbi_resume_payment_after_auth_v1";

function normalizeSelectedAddOnIds(selectedAddOns = {}) {
  return Object.keys(selectedAddOns)
    .filter((id) => selectedAddOns[id] && !id.endsWith("_val"))
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
}

function buildCheckoutPaymentSignature(bookingData, selectedAddOns = {}) {
  if (!bookingData || typeof bookingData !== "object") return "";
  const firstRide = bookingData?.rides?.[0] || bookingData?.trips?.[0] || {};
  const payload = {
    ride_type: Number(bookingData?.ride_type || 0),
    booking_type: Number(bookingData?.booking_type || 0),
    vehicle_type: Number(firstRide?.vehicle_type || 0),
    pickup_date: String(firstRide?.pickup_date || ""),
    pickup_time: String(firstRide?.pickup_time || ""),
    pickup_location: String(
      bookingData?.pickup || firstRide?.pickup_location || "",
    ).trim(),
    drop_location: String(
      bookingData?.dropoff || firstRide?.drop_location || "",
    ).trim(),
    pickup_lat: Number(
      bookingData?.pickup_lat ?? firstRide?.pickup_lat ?? 0,
    ).toFixed(5),
    pickup_lng: Number(
      bookingData?.pickup_lng ?? firstRide?.pickup_lng ?? 0,
    ).toFixed(5),
    drop_lat: Number(bookingData?.dropoff_lat ?? firstRide?.drop_lat ?? 0).toFixed(
      5,
    ),
    drop_lng: Number(bookingData?.dropoff_lng ?? firstRide?.drop_lng ?? 0).toFixed(
      5,
    ),
    return_date: String(firstRide?.return_date || ""),
    return_time: String(firstRide?.return_time || ""),
    addon_ids: normalizeSelectedAddOnIds(selectedAddOns),
  };
  return JSON.stringify(payload);
}

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();

  /** Stale "Failed to check fare" toasts from booking page should not linger on checkout. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    toast.dismiss(CHECK_FARE_TOAST_ID);
  }, []);
  /** Require real object so we don't fall through to stale sessionStorage when bookingData is undefined. */
  const hasBookingPayloadInState =
    location.state != null &&
    Object.prototype.hasOwnProperty.call(location.state, "bookingData") &&
    location.state.bookingData != null;

  const bookingData = useMemo(() => {
    const st = location.state;
    const navRev =
      typeof st?._checkoutRevision === "number" ? st._checkoutRevision : 0;

    let draft = null;
    try {
      const raw = sessionStorage.getItem(BOOKING_DRAFT_STORAGE_KEY);
      if (raw) draft = JSON.parse(raw);
    } catch {
      /* ignore */
    }

    let checkoutBookingRaw = null;
    try {
      checkoutBookingRaw = sessionStorage.getItem(CHECKOUT_BOOKING_STORAGE_KEY);
    } catch {
      checkoutBookingRaw = null;
    }

    const { data: resolved } = resolveCheckoutBookingData({
      navRev,
      hasBookingPayloadInState,
      stateBookingData: st?.bookingData,
      draft,
      checkoutBookingRaw,
    });

    return resolved;
  }, [hasBookingPayloadInState, location.state]);

  useEffect(() => {
    if (!bookingData) return;
    try {
      sessionStorage.setItem(
        CHECKOUT_BOOKING_STORAGE_KEY,
        JSON.stringify(bookingData),
      );
    } catch (_) {
      /* ignore quota / private mode */
    }
  }, [bookingData]);

  const { vehicles } = useVehicles();

  const firstRide = bookingData?.rides?.[0] || bookingData?.trips?.[0] || {};

  const onwardDistanceKm = useMemo(
    () => resolveOnwardDistanceKm(bookingData),
    [bookingData],
  );
  const returnDistanceKm = useMemo(
    () => resolveReturnDistanceKm(bookingData),
    [bookingData],
  );
  const onwardDistanceLabel = useMemo(
    () => formatDistanceKmForDisplay(onwardDistanceKm),
    [onwardDistanceKm],
  );
  const returnDistanceLabel = useMemo(
    () => formatDistanceKmForDisplay(returnDistanceKm),
    [returnDistanceKm],
  );
  const totalDistanceLabel = useMemo(() => {
    const parts = [onwardDistanceKm, returnDistanceKm].filter((n) =>
      Number.isFinite(Number(n)),
    );
    if (!parts.length) return null;
    return formatDistanceKmForDisplay(
      parts.reduce((sum, n) => sum + Number(n), 0),
    );
  }, [onwardDistanceKm, returnDistanceKm]);

  /** Round trip UI: booking_type 2 + return schedule or ride return_date/time (same rule as merge/sanitize). */
  const isRoundTripCheckout = useMemo(() => {
    if (!bookingData) return false;
    if (Number(bookingData.booking_type) !== 2) return false;
    return hasRoundTripLegData(bookingData);
  }, [bookingData]);

  const selectedVehicleId =
    firstRide?.vehicle_type ??
    firstRide?.vehicle_type_id ??
    bookingData?.vehicle_type ??
    bookingData?.vehicle_type_id ??
    null;

  const selectedVehicle = vehicles.find(
    (v) => Number(v.id) === Number(selectedVehicleId),
  );
  const checkoutVehicleImage = resolveCheckoutVehicleImage(selectedVehicle);

  /** Same resolution as create-booking payload (root + first ride leg). */
  const displayPassengers =
    Number(bookingData?.no_of_passengers ?? firstRide?.no_of_passengers) || 1;
  const rawSuitcaseForDisplay =
    bookingData?.suitcase ?? firstRide?.suitcase;
  const displaySuitcase =
    rawSuitcaseForDisplay === undefined || rawSuitcaseForDisplay === null
      ? 0
      : Math.max(0, Math.floor(Number(rawSuitcaseForDisplay)) || 0);

  const [loading] = useState(false);
  /** Always derived from latest bookingData (fixes stale totals after edit → re-check fare → checkout). */
  const bookingInfo = useMemo(() => {
    if (!bookingData?.fare_details) return null;
    return {
      ...bookingData.fare_details,
      partial_amount: bookingData.fare_details?.partial_amount || 0,
    };
  }, [bookingData]);

  const outstationInquiryOnly =
    Number(bookingData?.ride_type) === RIDE_OUTSTATION &&
    !!bookingInfo?.outstation_inquiry_only;
  const fareIsApproximate = !!bookingInfo?.fare_is_approximate;
  const supportFinalPriceMessage =
    bookingInfo?.support_final_price_message ||
    (outstationInquiryOnly
      ? "Please send your inquiry, Our support team will contact you shortly to confirm the final amount."
      : null);
  const [payLoading, setPayLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("full");
  // full | partial
  const [isInquiry, setIsInquiry] = useState(false);
  const [inquiryDetails, setInquiryDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (outstationInquiryOnly) {
      setIsInquiry(true);
      setPaymentMode("full");
    }
  }, [outstationInquiryOnly]);
  const [addOns, setAddOns] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState({}); // { id: value }
  const [showGuestModal, setShowGuestModal] = useState(false); // modal visibility only
  const [isGuestBooking, setIsGuestBooking] = useState(false); // toggle state (should persist after modal close)
  const [guestDetails, setGuestDetails] = useState({ name: "", phone: "" });
  const [guestDraft, setGuestDraft] = useState({ name: "", phone: "" });
  const [loginPhone, setLoginPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [showOtpField, setShowOtpField] = useState(false);
  const [requiresProfileCompletion, setRequiresProfileCompletion] =
    useState(false);
  const [pendingRegistrationToken, setPendingRegistrationToken] = useState("");
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    is_adult_confirmed: true,
  });
  const [loginLoading, setLoginLoading] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("user_token"),
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [resumePaymentAfterLogin, setResumePaymentAfterLogin] = useState(false);
  const [resumeInquiryAfterLogin, setResumeInquiryAfterLogin] = useState(false);
  const inquiryResumeAttemptedRef = useRef(false);
  const paymentResumeAttemptedRef = useRef(false);
  const handleInquiryRef = useRef(null);
  const proceedToPaymentRef = useRef(null);

  const clearResumeAuthIntents = () => {
    sessionStorage.removeItem(RESUME_INQUIRY_AFTER_AUTH_KEY);
    sessionStorage.removeItem(RESUME_PAYMENT_AFTER_AUTH_KEY);
  };

  const setResumeAuthIntent = (mode) => {
    if (mode === "inquiry") {
      sessionStorage.setItem(RESUME_INQUIRY_AFTER_AUTH_KEY, "1");
      sessionStorage.removeItem(RESUME_PAYMENT_AFTER_AUTH_KEY);
      return;
    }
    sessionStorage.setItem(RESUME_PAYMENT_AFTER_AUTH_KEY, "1");
    sessionStorage.removeItem(RESUME_INQUIRY_AFTER_AUTH_KEY);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.first_name || user.last_name || user.email || user.phone_number) {
      setInquiryDetails({
        first_name:
          user.first_name || (user.name ? user.name.split(" ")[0] : ""),
        last_name:
          user.last_name ||
          (user.name ? user.name.split(" ").slice(1).join(" ") : ""),
        email: user.email || "",
        phone: user.phone_number || "",
      });
    }
  }, []);

  useEffect(() => {
    if (!bookingData) {
      toast.error("No booking data found. Redirecting...");
      try {
        sessionStorage.removeItem(CHECKOUT_BOOKING_STORAGE_KEY);
        sessionStorage.removeItem(ACTIVE_CHECKOUT_BOOKING_KEY);
      } catch (_) {}
      setTimeout(() => navigate("/booking/airport"), 2000);
      return;
    }

    if (
      Number(bookingData.ride_type) === RIDE_OUTSTATION &&
      Number(bookingData.booking_type) === 2
    ) {
      const firstRide = bookingData.rides?.[0] || bookingData.trips?.[0];
      const onwardSchedule =
        bookingData.schedule ||
        (firstRide?.pickup_date && firstRide?.pickup_time
          ? `${firstRide.pickup_date} ${firstRide.pickup_time}`.trim()
          : null);
      const returnSchedule =
        bookingData.return_schedule ||
        (firstRide?.return_date && firstRide?.return_time
          ? `${firstRide.return_date} ${firstRide.return_time}`.trim()
          : null);

      const { validateOutstationRoundTripReturnWindow } = require("../utils/outstationReturnWindow");
      const v = validateOutstationRoundTripReturnWindow(onwardSchedule, returnSchedule);
      
      if (!v.ok) {
        toast.error("Invalid return time. Please re-select dates on the booking page.");
        setTimeout(() => navigate("/booking/outstation", { state: { editData: bookingData } }), 2000);
        return;
      }
    }

    // Fetch dynamic settings
    const fetchSettings = async () => {
      try {
        const firstRideForSettings =
          bookingData?.rides?.[0] || bookingData?.trips?.[0] || {};
        const airportId =
          Number(bookingData?.airport_id) ||
          Number(firstRideForSettings?.airport_id) ||
          null;
        const settingsUrl = new URL(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/ride-settings/${bookingData.ride_type}`,
        );
        if (Number(bookingData?.ride_type) === RIDE_AIRPORT && airportId) {
          settingsUrl.searchParams.set("airport_id", String(airportId));
        }
        const response = await axios.get(
          settingsUrl.toString(),
        );
        const payload = response?.data || {};
        const addOnsData = payload.add_ons ?? payload.data?.add_ons ?? [];
        const policiesData = payload.policies ?? payload.data?.policies ?? [];

        if (payload.status) {
          setAddOns(Array.isArray(addOnsData) ? addOnsData : []);
          setPolicies(Array.isArray(policiesData) ? policiesData : []);
        } else {
          setAddOns([]);
          setPolicies([]);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        setAddOns([]);
        setPolicies([]);
      }
    };
    fetchSettings();
  }, [bookingData, navigate]);

  const createInitialBooking = async (isInquiryMode = false, inquiryData = {}) => {
    // This will be called inside handlePayNow and handleInquiry
    try {
      const scheduleDate = bookingData.schedule
        ? new Date(bookingData.schedule)
        : new Date();
      const formattedDate =
        firstRide?.pickup_date || scheduleDate.toISOString().split("T")[0];
      const formattedTime =
        firstRide?.pickup_time ||
        scheduleDate.toTimeString().split(" ")[0].substring(0, 5); // HH:mm

      const selectedAddOnsPayload = Object.keys(selectedAddOns)
        .filter((id) => selectedAddOns[id] && !id.endsWith("_val"))
        .map((id) => {
          const addon = addOns.find((a) => a.id === parseInt(id));
          if (!addon) return null;
          return {
            addon_id: addon.id,
            name: addon.name,
            price: Number(addon.price),
            details: {},
          };
        })
        .filter(Boolean);

      const returnScheduleDate =
        Number(bookingData.booking_type) === 2 && bookingData.return_schedule
          ? new Date(bookingData.return_schedule)
          : null;

      const formattedReturnTime =
        firstRide?.return_time ||
        (returnScheduleDate
          ? returnScheduleDate.toTimeString().split(" ")[0].substring(0, 5)
          : null);

      const returnDate =
        firstRide?.return_date ||
        (returnScheduleDate ? returnScheduleDate.toISOString().split("T")[0] : null);

      const pickupLocation =
        bookingData.pickup || firstRide.pickup_location || firstRide.pickup || null;
      const dropLocation =
        bookingData.dropoff || firstRide.drop_location || firstRide.dropoff || null;

      const numPassengers =
        Number(bookingData.no_of_passengers ?? firstRide.no_of_passengers) || 1;
      const rawSuitcase = Number(bookingData.suitcase ?? firstRide.suitcase);
      const numSuitcase = Number.isFinite(rawSuitcase)
        ? Math.max(0, Math.floor(rawSuitcase))
        : 0;

      const ridePayload = {
        vehicle_type: firstRide?.vehicle_type || 1,
        pickup_location: pickupLocation,
        pickup_lat: bookingData.pickup_lat ?? firstRide.pickup_lat ?? null,
        pickup_lng: bookingData.pickup_lng ?? firstRide.pickup_lng ?? null,
        drop_location: dropLocation,
        drop_lat: bookingData.dropoff_lat ?? firstRide.drop_lat ?? null,
        drop_lng: bookingData.dropoff_lng ?? firstRide.drop_lng ?? null,
        pickup_date: formattedDate,
        pickup_time: formattedTime,
        no_of_passengers: numPassengers,
        suitcase: numSuitcase,
        return_time: formattedReturnTime,
        terminal: bookingData.terminal,
        terminal_id: bookingData.terminal_id || null,
        airport_id: bookingData.airport_id || null,
        airport_direction: bookingData.airport_direction || null,
        stops: bookingData.stops || [],
        return_stops: bookingData.return_stops || [],
        city_hours: bookingData.city_hours,
        flight_no: bookingData.flight_no,
        return_terminal_id: bookingData.return_terminal_id || null,
        return_flight_no: bookingData.return_flight_no || null,
      };

      // Add return trip location fields for round trip
      if (Number(bookingData.booking_type) === 2) {
        ridePayload.return_date = returnDate;
        ridePayload.return_pickup_location =
          bookingData.return_pickup ||
          firstRide.return_pickup_location ||
          dropLocation;
        ridePayload.return_pickup_lat =
          bookingData.return_pickup_lat ||
          firstRide.return_pickup_lat ||
          bookingData.dropoff_lat ||
          firstRide.drop_lat;
        ridePayload.return_pickup_lng =
          bookingData.return_pickup_lng ||
          firstRide.return_pickup_lng ||
          bookingData.dropoff_lng ||
          firstRide.drop_lng;
        ridePayload.return_drop_location =
          bookingData.return_dropoff ||
          firstRide.return_drop_location ||
          pickupLocation;
        ridePayload.return_drop_lat =
          bookingData.return_dropoff_lat ||
          firstRide.return_drop_lat ||
          bookingData.pickup_lat ||
          firstRide.pickup_lat;
        ridePayload.return_drop_lng =
          bookingData.return_dropoff_lng ||
          firstRide.return_drop_lng ||
          bookingData.pickup_lng ||
          firstRide.pickup_lng;
      }

      const missingRequiredFields = [];
      if (!ridePayload.pickup_location) missingRequiredFields.push("pickup_location");
      if (!ridePayload.drop_location) missingRequiredFields.push("drop_location");
      if (ridePayload.pickup_lat == null) missingRequiredFields.push("pickup_lat");
      if (ridePayload.pickup_lng == null) missingRequiredFields.push("pickup_lng");
      if (ridePayload.drop_lat == null) missingRequiredFields.push("drop_lat");
      if (ridePayload.drop_lng == null) missingRequiredFields.push("drop_lng");

      if (Number(bookingData.booking_type) === 2) {
        if (!ridePayload.return_pickup_location) {
          missingRequiredFields.push("return_pickup_location");
        }
        if (!ridePayload.return_drop_location) {
          missingRequiredFields.push("return_drop_location");
        }
        if (ridePayload.return_pickup_lat == null) {
          missingRequiredFields.push("return_pickup_lat");
        }
        if (ridePayload.return_pickup_lng == null) {
          missingRequiredFields.push("return_pickup_lng");
        }
        if (ridePayload.return_drop_lat == null) {
          missingRequiredFields.push("return_drop_lat");
        }
        if (ridePayload.return_drop_lng == null) {
          missingRequiredFields.push("return_drop_lng");
        }
      }

      if (missingRequiredFields.length) {
        throw new Error(
          `Missing required booking fields: ${missingRequiredFields.join(", ")}`,
        );
      }

      const payload = {
        ride_type: bookingData.ride_type,
        booking_type: Number(bookingData.booking_type),
        no_of_passengers: numPassengers,
        suitcase: numSuitcase,
        same_dv_multiday: bookingData.same_dv_multiday ? 1 : 0,
        payment_mode: isInquiryMode ? "inquiry" : paymentMode,
        add_ons: selectedAddOnsPayload,
        rides: [ridePayload],
        is_inquiry: isInquiryMode || false,
        terminal_charge: Number(
          bookingData.fare_details?.terminal_charge || 0,
        ),
        // Always send partial_amount so it stays saved in DB for "pay later" use-cases.
        // paid_amount will still be updated only by actual payments (gateway/admin).
        partial_amount: Number(calculatePartialAmount()),
        book_for_guest:
          isGuestBooking && guestDetails.name && guestDetails.phone
            ? { name: guestDetails.name, phone: guestDetails.phone }
            : undefined,
        ...inquiryData,
        booking_source: "website",
      };

      const token = localStorage.getItem("user_token");
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/create-booking`,
        payload,
        { headers },
      );
      return response.data;
    } catch (error) {
      console.error("Booking creation error:", error);
      throw error;
    }
  };

  const handleInquiry = async () => {
    setPayLoading(true);
    try {
      const response = await createInitialBooking(true, inquiryDetails);

      if (response.status) {
        toast.success("Inquiry sent successfully!");
        setInquiryDetails({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
        });
        setTimeout(() => navigate("/bookings"), 3000);
      } else {
        toast.error(response.message || "Failed to send inquiry");
      }
    } catch (error) {
      console.error("Inquiry error:", error);
      toast.error(
        error?.response?.data?.message ||
        "Error sending inquiry. Please try again.",
      );
    } finally {
      setPayLoading(false);
    }
  };

  handleInquiryRef.current = handleInquiry;

  const calculateTotalFare = () => {
    if (!bookingInfo) return 0;
    let total = Number(
      bookingInfo.total_customer_amount || bookingInfo.total_amount || 0,
    );

    addOns.forEach((addon) => {
      if (selectedAddOns[addon.id]) {
        total += Number(addon.price);
      }
    });

    return Number(total).toFixed(2);
  };

  const calculatePartialAmount = () => {
    const total = Number(calculateTotalFare());
    // Use the percentage from the original partial_amount if available
    if (bookingInfo?.partial_amount && bookingInfo?.total_amount) {
      const percentage =
        (Number(bookingInfo.partial_amount) /
          Number(bookingInfo.total_amount)) *
        100;
      return ((total * percentage) / 100).toFixed(2);
    }
    return total.toFixed(2);
  };

  const calculatePayableNow = () => {
    if (isInquiry) return "0.00";
    if (paymentMode === "full") return calculateTotalFare();
    return calculatePartialAmount();
  };

  const calculateRemainingAmount = () => {
    const total = Number(calculateTotalFare());
    const payable = Number(calculatePayableNow());
    return (total - payable).toFixed(2);
  };

  const proceedToPayment = async () => {
    setPayLoading(true);
    try {
      const currentSignature = buildCheckoutPaymentSignature(
        bookingData,
        selectedAddOns,
      );
      let activeContext = null;
      try {
        activeContext = JSON.parse(
          sessionStorage.getItem(ACTIVE_CHECKOUT_BOOKING_KEY) || "null",
        );
      } catch {
        activeContext = null;
      }

      const canReuseActiveBooking =
        activeContext &&
        Number(activeContext.bookingId) > 0 &&
        String(activeContext.signature || "") === currentSignature;

      if (canReuseActiveBooking) {
        try {
          await launchBookingPayment({
            bookingId: Number(activeContext.bookingId),
            payType: paymentMode,
          });
          return;
        } catch (reuseError) {
          try {
            sessionStorage.removeItem(ACTIVE_CHECKOUT_BOOKING_KEY);
          } catch (_) {
            /* ignore */
          }
          const httpStatus = Number(reuseError?.response?.status || 0);
          if (httpStatus === 410) {
            toast.error(
              reuseError?.response?.data?.message ||
                "Payment already completed for this booking.",
            );
            setPayLoading(false);
            return;
          }
        }
      }

      const bookingResponse = await createInitialBooking();
      if (bookingResponse.status) {
        const bookingId =
          bookingResponse.booking_id ||
          bookingResponse.booking?.data?.id ||
          bookingResponse.booking?.id;
        if (!Number(bookingId)) {
          throw new Error("Booking id missing in create-booking response.");
        }
        if (Number(bookingId) > 0) {
          try {
            sessionStorage.setItem(
              ACTIVE_CHECKOUT_BOOKING_KEY,
              JSON.stringify({
                bookingId: Number(bookingId),
                payType: paymentMode,
                signature: currentSignature,
                reusedBooking: Boolean(bookingResponse.reused_booking),
                updatedAt: Date.now(),
              }),
            );
          } catch (_) {
            /* ignore */
          }
        }
        await launchBookingPayment({ bookingId, payType: paymentMode });
      } else {
        toast.error(bookingResponse.message || "Failed to create booking");
        setPayLoading(false);
      }
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
      setPayLoading(false);
    }
  };
  proceedToPaymentRef.current = proceedToPayment;

  useEffect(() => {
    if (!isAuthenticated) return;
    const shouldResumeInquiry =
      sessionStorage.getItem(RESUME_INQUIRY_AFTER_AUTH_KEY) === "1";
    const shouldResumePayment =
      sessionStorage.getItem(RESUME_PAYMENT_AFTER_AUTH_KEY) === "1";

    if (shouldResumeInquiry && !inquiryResumeAttemptedRef.current) {
      inquiryResumeAttemptedRef.current = true;
      paymentResumeAttemptedRef.current = true;
      clearResumeAuthIntents();
      setIsInquiry(true);
      handleInquiryRef.current?.();
      return;
    }

    if (shouldResumePayment && !paymentResumeAttemptedRef.current) {
      paymentResumeAttemptedRef.current = true;
      inquiryResumeAttemptedRef.current = true;
      clearResumeAuthIntents();
      setIsInquiry(false);
      proceedToPaymentRef.current?.();
    }
  }, [isAuthenticated]);

  const handlePayNow = async (e) => {
    e.preventDefault();

    if (isInquiry || outstationInquiryOnly) {
      if (isAuthenticated) {
        handleInquiry();
      } else {
        setResumePaymentAfterLogin(false);
        setResumeInquiryAfterLogin(true);
        setResumeAuthIntent("inquiry");
        setShowLoginModal(true);
      }
      return;
    }

    if (!isAuthenticated) {
      localStorage.setItem("pendingBooking", JSON.stringify(bookingData));
      setResumeAuthIntent("payment");
      setResumeInquiryAfterLogin(false);
      setResumePaymentAfterLogin(true);
      setShowLoginModal(true);
      return;
    }

    proceedToPayment();
  };

  const handleSendGuestOtp = async () => {
    if (!loginPhone || !/^\d{10}$/.test(loginPhone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setLoginLoading(true);
    setRequiresProfileCompletion(false);
    setPendingRegistrationToken("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/login`,
        { phone: loginPhone, source: "website" },
      );
      if (response.data.status) {
        toast.success("OTP sent successfully!");
        setShowOtpField(true);
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Error sending OTP. Please try again.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyGuestOtp = async () => {
    const finalOtp = otp.join("");
    if (finalOtp.length < 4) {
      toast.error("Please enter 4-digit OTP");
      return;
    }
    setLoginLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/verify-otp`,
        { phone: loginPhone, otp: finalOtp },
      );
      if (response.data.status) {
        if (response.data.is_new_user === true) {
          // toast.info("Please complete your registration details.");
          setShowOtpField(false);
          setRequiresProfileCompletion(true);
          setPendingRegistrationToken(response.data.token || "");
          return;
        }
        localStorage.setItem("user_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.customer));
        localStorage.setItem("user_data", JSON.stringify(response.data.customer));
        toast.success(response.data.message || "Login successful!");
        localStorage.removeItem("pendingBooking"); // Clear pending booking after successful login
        setIsAuthenticated(true);
        setShowLoginModal(false);
        setShowGuestModal(false);
        setShowOtpField(false);
        setRequiresProfileCompletion(false);
        setPendingRegistrationToken("");
        setOtp(["", "", "", ""]);
        clearResumeAuthIntents();
        window.dispatchEvent(new Event("loginStateChange"));
        if (resumeInquiryAfterLogin) {
          inquiryResumeAttemptedRef.current = true;
          paymentResumeAttemptedRef.current = true;
          setResumeInquiryAfterLogin(false);
          setResumePaymentAfterLogin(false);
          handleInquiry();
        } else if (resumePaymentAfterLogin) {
          paymentResumeAttemptedRef.current = true;
          inquiryResumeAttemptedRef.current = true;
          setResumeInquiryAfterLogin(false);
          setResumePaymentAfterLogin(false);
          proceedToPayment();
        }
      } else {
        toast.error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Error verifying OTP. Please try again.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCompleteGuestRegistration = async () => {
    if (!profileData.first_name.trim() || !profileData.last_name.trim()) {
      toast.error("Please enter first name and last name");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(profileData.first_name.trim())) {
      toast.error("First name may only contain letters.");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(profileData.last_name.trim())) {
      toast.error("Last name may only contain letters.");
      return;
    }
    const email = profileData.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!profileData.is_adult_confirmed) {
      toast.error("You must be at least 18 years old to continue.");
      return;
    }
    if (!pendingRegistrationToken) {
      toast.error("Session expired. Please verify OTP again.");
      setRequiresProfileCompletion(false);
      setShowOtpField(true);
      return;
    }

    setLoginLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/complete-registration`,
        {
          first_name: profileData.first_name.trim(),
          last_name: profileData.last_name.trim(),
          email: email || null,
          is_adult_confirmed: profileData.is_adult_confirmed ? 1 : 0,
        },
        {
          headers: { Authorization: `Bearer ${pendingRegistrationToken}` },
        },
      );

      if (response.data.status) {
        localStorage.setItem("user_token", pendingRegistrationToken);
        localStorage.setItem("user", JSON.stringify(response.data.customer));
        localStorage.setItem("user_data", JSON.stringify(response.data.customer));
        toast.success(response.data.message || "Registration completed!");
        localStorage.removeItem("pendingBooking");
        setIsAuthenticated(true);
        setShowLoginModal(false);
        setShowGuestModal(false);
        setShowOtpField(false);
        setRequiresProfileCompletion(false);
        setPendingRegistrationToken("");
        setOtp(["", "", "", ""]);
        clearResumeAuthIntents();
        window.dispatchEvent(new Event("loginStateChange"));
        if (resumeInquiryAfterLogin) {
          inquiryResumeAttemptedRef.current = true;
          paymentResumeAttemptedRef.current = true;
          setResumeInquiryAfterLogin(false);
          setResumePaymentAfterLogin(false);
          handleInquiry();
        } else if (resumePaymentAfterLogin) {
          paymentResumeAttemptedRef.current = true;
          inquiryResumeAttemptedRef.current = true;
          setResumeInquiryAfterLogin(false);
          setResumePaymentAfterLogin(false);
          proceedToPayment();
        }
      } else {
        toast.error(response.data.message || "Failed to complete registration");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to complete registration. Please try again.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendGuestOtp = async () => {
    setResendingOtp(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/resend-otp`,
        { phone: loginPhone },
      );
      toast.success(response.data.message || "OTP resent!");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Error sending OTP. Please try again.",
      );
    } finally {
      setResendingOtp(false);
    }
  };

  if (loading && isAuthenticated) {
    return (
      <div className="section_pt section_pb text-center">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Preparing your trip details...</p>
      </div>
    );
  }

  const rideTitle =
    bookingData?.ride_type === RIDE_AIRPORT
      ? "Airport Transfer"
      : bookingData?.ride_type === RIDE_OUTSTATION
        ? "Outstation Trip"
        : bookingData?.ride_type === RIDE_CITYTOUR
          ? "City Tour"
          : "Trip Summary";

  const returnPickupDisplay =
    bookingData?.return_pickup ||
    firstRide?.return_pickup_location ||
    bookingData?.dropoff ||
    "-";
  const returnDropDisplay =
    bookingData?.return_dropoff ||
    firstRide?.return_drop_location ||
    bookingData?.pickup ||
    "-";
  const returnTimeRaw =
    bookingData?.return_schedule ||
    [
      firstRide?.return_date || "",
      firstRide?.return_time || "",
    ]
      .filter(Boolean)
      .join(" - ") ||
    "-";

  const returnTimeDisplay =
    returnTimeRaw === "-"
      ? "-"
      : formatScheduleLikeDatePicker(returnTimeRaw);

  const outwardPickupTimeDisplay = formatScheduleLikeDatePicker(
    bookingData?.schedule ||
      (firstRide?.pickup_date && firstRide?.pickup_time
        ? `${firstRide.pickup_date} ${firstRide.pickup_time}`.trim()
        : ""),
  );

  const isAirportRide = Number(bookingData?.ride_type) === RIDE_AIRPORT;
  const isPickupDirection = bookingData?.airport_direction === "pickup";
  const outwardPickupTerminal =
    isAirportRide && isPickupDirection ? bookingData?.terminal : null;
  const outwardDropTerminal =
    isAirportRide && !isPickupDirection ? bookingData?.terminal : null;
  const returnPickupTerminal =
    isAirportRide && !isPickupDirection ? bookingData?.return_terminal : null;
  const returnDropTerminal =
    isAirportRide && isPickupDirection ? bookingData?.return_terminal : null;

  return (
    <>
      <section className="checkout_wrap section_pt section_pb">
        <div className="container">
          <div className="titles_checkout d-flex align-items-center justify-content-between">
            <h2>{rideTitle}</h2>
            <button
              type="button"
              className="btn btn-warning fw-bold px-4 mb-2 py-2 rounded-pill shadow-sm mt-2"
              onClick={() => navigate("/")}
            >
              Back to Home
            </button>
          </div>
          <div className="row">
            <div className="col-lg-3 col-md-3 col-sm-12 col-12">
              <div className="trip_summary">
                <div className="trip_title">
                  <h3>TRIP SUMMARY</h3>
                  <button
                    onClick={() => {
                      const type =
                        bookingData.ride_type === RIDE_AIRPORT
                          ? "airport"
                          : bookingData.ride_type === RIDE_OUTSTATION
                            ? "outstation"
                            : "city";
                      try {
                        sessionStorage.removeItem(CHECKOUT_BOOKING_STORAGE_KEY);
                        sessionStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
                        sessionStorage.removeItem(ACTIVE_CHECKOUT_BOOKING_KEY);
                      } catch (_) {}
                      navigate(`/booking/${type}`, {
                        replace: true,
                        state: {
                          editData: bookingData,
                          checkoutRevisionRef:
                            typeof location.state?._checkoutRevision === "number"
                              ? location.state._checkoutRevision
                              : 0,
                        },
                      });
                    }}
                    className="edit btn btn-link p-0"
                    style={{ textDecoration: "none", color: "#ffc100", fontWeight: "700" }}
                  >
                  <FontAwesomeIcon icon={faPenToSquare} />
                    EDIT
                  </button>
                </div>
                {bookingData?.ride_type === RIDE_CITYTOUR && (
                  <div className="trip_location">
                    <img
                      src="/assets/img/all-images/Clock-icon-yellow.png"
                      alt=""
                    />
                    <h3>
                      <small>City Tour</small> <br />
                      {bookingData?.city_hours} Hours
                    </h3>
                  </div>
                )}
                <div className="trip_summaryDetail_row">
                  <div className="trip_location trip_locationDetail trip_locationDetail2">
                    <img
                      src="/assets/img/all-images/location_on-yellow.png"
                      alt=""
                    />
                    <h3>
                      <small>Pick up</small> <br />
                      {bookingData?.pickup}{" "}
                      {outwardPickupTerminal ? `(${outwardPickupTerminal})` : ""}
                    </h3>
                  </div>
                  {bookingData?.stops?.map((stop, idx) => (
                    <div
                      className="trip_location trip_locationDetail trip_locationDetail2"
                      key={idx}
                    >
                      <img
                        src="/assets/img/all-images/location_on-yellow.png"
                        alt=""
                      />
                      <h3>
                        <small>Stop {idx + 1}</small> <br />
                        {stop.address}
                      </h3>
                    </div>
                  ))}
                  <div className="trip_location trip_locationDetail trip_locationDetail2">
                    <img
                      src="/assets/img/all-images/location_on-yellow.png"
                      alt=""
                    />
                    <h3>
                      <small>Drop</small> <br />
                      {bookingData?.dropoff}{" "}
                      {outwardDropTerminal ? `(${outwardDropTerminal})` : ""}
                    </h3>
                  </div>
                </div>
                <div className="trip_location">
                  <img
                    src="/assets/img/all-images/Calendar-yellow.png"
                    alt=""
                  />
                  <h3>
                    <small>Pickup time</small> <br />
                    {outwardPickupTimeDisplay}
                  </h3>
                </div>
                <div
                  className="trip_location"
                  style={
                    isRoundTripCheckout
                      ? { alignItems: "flex-start" }
                      : undefined
                  }
                >
                  <img
                    src="/assets/img/all-images/distance.png"
                    alt=""
                  />
                  <h3>
                    <small>Approximate Distance</small> <br />
                    {isRoundTripCheckout ? (
                      <span
                        style={{
                          display: "block",
                          marginTop: 4,
                          minWidth: 140,
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            fontSize: 13,
                            fontWeight: 400,
                            color: "#333",
                            lineHeight: 1.7,
                          }}
                        >
                          <span>Onward</span>
                          <span>
                            {onwardDistanceLabel != null
                              ? `${onwardDistanceLabel} km`
                              : "—"}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            fontSize: 13,
                            fontWeight: 400,
                            color: "#333",
                            lineHeight: 1.7,
                          }}
                        >
                          <span>Return</span>
                          <span>
                            {returnDistanceLabel != null
                              ? `${returnDistanceLabel} km`
                              : "—"}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            marginTop: 6,
                            paddingTop: 6,
                            borderTop: "1px solid #ffe082",
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111",
                            lineHeight: 1.4,
                          }}
                        >
                          <span>Total</span>
                          <span>
                            {totalDistanceLabel != null
                              ? `${totalDistanceLabel} km`
                              : "—"}
                          </span>
                        </span>
                      </span>
                    ) : onwardDistanceLabel != null ? (
                      `${onwardDistanceLabel} km`
                    ) : (
                      "—"
                    )}
                  </h3>
                </div>

                {isRoundTripCheckout && (
                  <div className="mt-4 pt-3 border-top">
                    <div className="trip_title">
                      <h3
                        className="text-uppercase"
                        style={{ fontSize: "14px", fontWeight: "bold" }}
                      >
                        Return Trip
                      </h3>
                    </div>
                    <div className="trip_summaryDetail_row">
                      <div className="trip_location trip_locationDetail trip_locationDetail2">
                        <img
                          src="/assets/img/all-images/location_on-yellow.png"
                          alt=""
                        />
                        <h3>
                          <small>Pick up</small> <br />
                          {returnPickupDisplay}{" "}
                          {returnPickupTerminal ? `(${returnPickupTerminal})` : ""}
                        </h3>
                      </div>
                      {bookingData?.return_stops?.map((stop, idx) => (
                        <div
                          className="trip_location trip_locationDetail trip_locationDetail2"
                          key={idx}
                        >
                          <img
                            src="/assets/img/all-images/location_on-yellow.png"
                            alt=""
                          />
                          <h3>
                            <small>Stop {idx + 1}</small> <br />
                            {stop.address}
                          </h3>
                        </div>
                      ))}
                      <div className="trip_location trip_locationDetail trip_locationDetail2">
                        <img
                          src="/assets/img/all-images/location_on-yellow.png"
                          alt=""
                        />
                        <h3>
                          <small>Drop</small> <br />
                          {returnDropDisplay}
                          {" "}
                          {returnDropTerminal ? `(${returnDropTerminal})` : ""}
                        </h3>
                      </div>
                    </div>
                    <div className="trip_location">
                      <img
                        src="/assets/img/all-images/Calendar-yellow.png"
                        alt=""
                      />
                      <h3>
                        <small>Pickup time</small> <br />
                        {returnTimeDisplay}
                      </h3>
                    </div>
                    {/* <div className="trip_location">
                      <img
                        src="/assets/img/all-images/distance.png"
                        alt=""
                      />
                      <h3>
                        <small>Distance</small> <br />
                        {returnDistanceLabel != null
                          ? `${returnDistanceLabel} km`
                          : "—"}
                      </h3>
                    </div> */}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="trip_detailMain">
                <div className="tripPrice_car">
                  <img
                    src={checkoutVehicleImage}
                    alt=""
                    // style={{ maxWidth: "150px" }}
                  />
                  <div className="tripPrice_right">
                    <h3>
                      ₹
                      {bookingInfo?.total_customer_amount ||
                        bookingInfo?.total_amount ||
                        "0"}
                    </h3>
                    {fareIsApproximate && (
                      <small className="d-block text-muted mb-1">
                        Approximate fare
                      </small>
                    )}
                    <span>
                      {selectedVehicle?.name || "Hatchback"} -{" "}
                      {displayPassengers} Guests - {displaySuitcase} Suitcase
                    </span>
                  </div>
                </div>
                <div className="addOn_col">
                  {addOns.length > 0 && <h3>Add-ons</h3>}
                  {addOns.map((addon) => (
                    <div className="addOn_row" key={addon.id}>
                      <div className="img_text">
                        <img src={addon.image_url} alt="" />
                        <h3>
                          {addon.name} <br />{" "}
                          <small>
                            {addon.price > 0 ? `₹${addon.price}, ` : ""}
                            {addon.description}
                          </small>
                        </h3>
                      </div>
                      <label className="toggle_check">
                        <input
                          className="toggle-checkbox d-none"
                          type="checkbox"
                          checked={selectedAddOns[addon.id] || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedAddOns((prev) => ({
                              ...prev,
                              [addon.id]: checked,
                            }));
                          }}
                        />
                        <div className="toggle-switch"></div>
                      </label>
                    </div>
                  ))}
                  {/* Hardcoded Book for someone else */}
                  <div className="addOn_row">
                    <div className="img_text">
                      <img
                        src="/assets/img/all-images/user-icon-yellow.png"
                        alt=""
                      />
                      <h3>
                        Book for someone else <br />{" "}
                        <small>
                          {guestDetails.name && guestDetails.phone
                            ? `${guestDetails.name} · +91 ${guestDetails.phone}`
                            : "Add the guest"}
                        </small>
                      </h3>
                    </div>
                    <label className="toggle_check">
                      <input
                        className="toggle-checkbox d-none"
                        type="checkbox"
                        checked={isGuestBooking}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setGuestDraft({
                              name: guestDetails?.name || "",
                              phone: guestDetails?.phone || "",
                            });
                            setShowGuestModal(true);
                          } else {
                            setShowGuestModal(false);
                            setIsGuestBooking(false);
                            setGuestDetails({ name: "", phone: "" });
                            setGuestDraft({ name: "", phone: "" });
                          }
                        }}
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </div>
                </div>
                <div className="addOn_col">
                  {policies.length > 0 && (
                    <>
                      <hr />
                      <h3>Policies</h3>
                    </>
                  )}
                  {policies.map((policy) => (
                    <div className="addOn_row" key={policy.id}>
                      <div className="img_text">
                        <img src={policy.image_url} alt="" />
                        <h3>
                          {policy.title} <br />{" "}
                          <small>{policy.description}</small>
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
                <hr />
                <div className="policy_pre">
                  <p>
                    We reserve the right to cancel the ride after 30 minutes of
                    waiting. No cancellation fee if we're late, but waiting
                    charges still apply.
                  </p>
                  <p>
                    By booking this ride, I consent to Orbicabs terms of
                    service.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn_more"
                    >
                      {" "}
                      Learn more
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-3 col-sm-12 col-12">
              <div className="trip_summary">
                <div className="trip_title">
                  <h3>Fare Summary</h3>
                </div>
                {/* <div className="base_fare">
                  <p>
                    <span>Base Fare</span>
                    <span>
                      ₹
                      {bookingInfo?.base_rate_per_km ||
                        bookingInfo?.base_fare ||
                        "0"}
                    </span>
                  </p>
                  <p>
                    <span>Platform Charges (Incl. GST)</span>
                    <span>
                      ₹
                      {(
                        (Number(bookingInfo?.commission_per_km) ||
                          Number(bookingInfo?.commission) ||
                          0) +
                        (Number(bookingInfo?.gst_on_commission) ||
                          Number(bookingInfo?.gst) ||
                          0)
                      ).toFixed(2)}
                    </span>
                  </p>
                </div>
                <hr /> */}
                <div className="base_fare total_amount">
                  <div className="total-fare-block">
                    <p className="mb-0">
                      <span>
                        {fareIsApproximate
                          ? "Approximate Fare"
                          : bookingInfo?.fixed_route_applied
                            ? "Fixed Fare"
                            : "Total Fare"}
                      </span>
                      <span>₹{calculateTotalFare()}</span>
                    </p>
                    <small className="text-muted total-fare-note">
                      {fareIsApproximate
                        ? "Approximate fare based on distance. Final amount may change."
                        : "(Incl. taxes, tolls & airport fee as applicable)"}
                    </small>
                    {supportFinalPriceMessage && (
                      <small
                        className="d-block mt-2"
                        style={{ color: "#8a6d00", fontWeight: 600 }}
                      >
                        {supportFinalPriceMessage}
                      </small>
                    )}
                  </div>
                  {!outstationInquiryOnly && (
                    <>
                      <p className="mt-2">
                        <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                          Payable Now
                        </span>
                        <span className="text-success" style={{ fontSize: "14px" }}>
                          ₹{calculatePayableNow()}
                        </span>
                      </p>
                      <p>
                        <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                          Remaining Amount
                        </span>
                        <span className="text-danger" style={{ fontSize: "14px" }}>
                          ₹{calculateRemainingAmount()}
                        </span>
                      </p>
                    </>
                  )}
                </div>
                <hr />
                {/* <div className="fare_summary">
                  <p>
                    Booking this Orbi saves 32.77 kg of CO2 emission. The same
                    amount 4 trees clean from the air in a year.
                  </p>
                </div> */}
                <div className="pay_type">
                  <form action="#">
                    {!outstationInquiryOnly && (
                      <>
                    <p>
                      <input
                        type="radio"
                        id="test1"
                        name="radio-group"
                        checked={paymentMode === "full" && !isInquiry}
                        onChange={() => {
                          setPaymentMode("full");
                          setIsInquiry(false);
                          setResumeInquiryAfterLogin(false);
                          if (!isAuthenticated) {
                            setResumeAuthIntent("payment");
                          } else {
                            clearResumeAuthIntents();
                          }
                        }}
                      />
                      <label htmlFor="test1">
                        <span>Fully Pay</span>
                        <span>₹{calculateTotalFare()}</span>
                      </label>
                    </p>
                    <p>
                      <input
                        type="radio"
                        id="test2"
                        name="radio-group"
                        checked={paymentMode === "partial" && !isInquiry}
                        onChange={() => {
                          setPaymentMode("partial");
                          setIsInquiry(false);
                          setResumeInquiryAfterLogin(false);
                          if (!isAuthenticated) {
                            setResumeAuthIntent("payment");
                          } else {
                            clearResumeAuthIntents();
                          }
                        }}
                      />
                      <label htmlFor="test2">
                        <span>Partial Pay</span>
                        <span>₹{calculatePartialAmount()}</span>
                      </label>
                    </p>
                      </>
                    )}
                    <p>
                      <input
                        type="radio"
                        id="test3"
                        name="radio-group"
                        checked={isInquiry || outstationInquiryOnly}
                        disabled={outstationInquiryOnly}
                        onChange={() => {
                          setIsInquiry(true);
                          setResumePaymentAfterLogin(false);
                          if (!isAuthenticated) {
                            setResumeAuthIntent("inquiry");
                          } else {
                            clearResumeAuthIntents();
                          }
                        }}
                      />
                      <label htmlFor="test3">
                        <span>Send Inquiry</span>
                      </label>
                    </p>
                  </form>
                </div>
                <div className="summary_btn">
                  <button
                    type="button"
                    className="btn"
                    onClick={handlePayNow}
                    disabled={payLoading || (isAuthenticated && !bookingInfo)}
                  >
                    {payLoading
                      ? "Processing..."
                      : isInquiry && !isAuthenticated
                        ? "Log in and Send Inquiry"
                        : isInquiry && isAuthenticated
                          ? "Send Inquiry"
                          : isInquiry
                            ? "Send Inquiry"
                            : isAuthenticated
                              ? "Book Now"
                              : "Log in and Book now"
                              }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WorkWithUs />
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Guest Booking Modal */}
      {showGuestModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content text-dark px-4 py-2 loginForm_inner">
              {!isAuthenticated ? (
                <div className="modal-body p-4 text-center">
                  <button
                    type="button"
                    className="btn-close float-end"
                    onClick={() => {
                      setShowGuestModal(false);
                      setShowOtpField(false);
                      setRequiresProfileCompletion(false);
                      setPendingRegistrationToken("");
                      setOtp(["", "", "", ""]);
                    }}
                  ></button>
                  <h4 className="fw-bold mb-4 mt-2">Please Log In First</h4>
                  {!showOtpField && !requiresProfileCompletion ? (
                    <>
                      <div className="mb-3 form-group">
                        <div className="input-group">
                          <span className="input-group-text">+91</span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Phone Number"
                            value={loginPhone}
                            onChange={(e) => setLoginPhone(e.target.value)}
                            maxLength={10}
                          />
                        </div>
                      </div>
                      <button
                        className="btn btn-warning w-100 fw-bold"
                        disabled={loginLoading}
                        onClick={handleSendGuestOtp}
                      >
                        {loginLoading ? "Sending..." : "Generate OTP"}
                      </button>
                    </>
                  ) : showOtpField ? (
                    <>
                      <p className="small mb-2">OTP sent to +91 {loginPhone}</p>
                      <div className="d-flex justify-content-center mb-3">
                        {otp.map((data, index) => (
                          <input
                            key={index}
                            type="text"
                            maxLength="1"
                            className="form-control text-center mx-1"
                            style={{ width: "45px" }}
                            value={data}
                            onChange={(e) => {
                              const newOtp = [...otp];
                              newOtp[index] = e.target.value;
                              setOtp(newOtp);
                              if (e.target.value && e.target.nextSibling) {
                                e.target.nextSibling.focus();
                              }
                            }}
                          />
                        ))}
                      </div>
                      <p className="small mb-4">
                        Didn't receive?{" "}
                        <a
                          href="#!"
                          className="text-warning fw-bold"
                          onClick={handleResendGuestOtp}
                        >
                          {resendingOtp ? "Resending..." : "Resend"}
                        </a>
                      </p>
                      <button
                        className="btn btn-warning w-100 fw-bold"
                        disabled={loginLoading}
                        onClick={handleVerifyGuestOtp}
                      >
                        {loginLoading ? "Verifying..." : "Verify & Login"}
                      </button>
                    </>
                  ) : (
                    <>
                      <h5 className="fw-bold mb-3">Complete Registration</h5>
                      <div className="mb-3 form-group text-start">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={profileData.first_name}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              first_name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3 form-group text-start">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={profileData.last_name}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              last_name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3 form-group text-start">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Optional"
                          value={profileData.email}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="mb-4 form-check d-flex align-items-center justify-content-center">
                        <input
                          type="checkbox"
                          id="guest_is_adult_confirmed"
                          className="form-check-input"
                          checked={profileData.is_adult_confirmed}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              is_adult_confirmed: e.target.checked,
                            }))
                          }
                        />
                        <label
                          htmlFor="guest_is_adult_confirmed"
                          className="ms-2 mb-0"
                        >
                          I am 18+ years old
                        </label>
                      </div>
                      <button
                        className="btn btn-warning w-100 fw-bold"
                        disabled={loginLoading}
                        onClick={handleCompleteGuestRegistration}
                      >
                        {loginLoading ? "Submitting..." : "Complete Registration"}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="modal-header border-0 pb-0 loginForm_inner">
                    <h5 className="modal-title w-100 text-center fw-bold">
                      Book for someone else
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowGuestModal(false);
                        if (!guestDetails?.name || !guestDetails?.phone) {
                          setIsGuestBooking(false);
                        }
                      }}
                    ></button>
                  </div>
                  <div className="modal-body p-0 mt-3">
                    <div className="mb-3 form-group">
                      <label className="form-label small fw-bold">
                        Guest Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter guest name"
                        value={guestDraft.name}
                        maxLength={50}
                        onChange={(e) =>
                          setGuestDraft((prev) => ({
                            ...prev,
                            name: e.target.value.replace(/[^a-zA-Z\s]/g, ""),
                          }))
                        }
                      />
                    </div>
                    <div className="mb-4 form-group">
                      <label className="form-label small fw-bold">
                        Guest Phone
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">+91</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          className="form-control"
                          placeholder="Enter 10-digit phone"
                          value={guestDraft.phone}
                          onChange={(e) =>
                            setGuestDraft((prev) => ({
                              ...prev,
                              phone: e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 10),
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="mt-4 mb-3">
                      <button
                        className="btn btn-warning w-100 fw-bold"
                        onClick={() => {
                          const name = guestDraft.name.trim().replace(/\s+/g, " ");
                          const phone = guestDraft.phone.replace(/\D/g, "");
                          if (!name) {
                            toast.error("Please enter guest name");
                            return;
                          }
                          if (!/^[a-zA-Z\s]+$/.test(name)) {
                            toast.error(
                              "Guest name may only contain letters.",
                            );
                            return;
                          }
                          if (!/^\d{10}$/.test(phone)) {
                            toast.error(
                              "Please enter a valid 10-digit phone number",
                            );
                            return;
                          }
                          setGuestDetails({ name, phone });
                          setIsGuestBooking(true);
                          setShowGuestModal(false);
                        }}
                      >
                        Add Guest
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && !isAuthenticated && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content text-dark px-4 py-2 loginForm_inner">
              <div className="modal-body p-4 text-center">
                <button
                  type="button"
                  className="btn-close float-end"
                  onClick={() => {
                    setShowLoginModal(false);
                    setResumePaymentAfterLogin(false);
                    setResumeInquiryAfterLogin(false);
                    clearResumeAuthIntents();
                    setShowOtpField(false);
                    setRequiresProfileCompletion(false);
                    setPendingRegistrationToken("");
                    setOtp(["", "", "", ""]);
                  }}
                ></button>
                {!showOtpField && !requiresProfileCompletion ? (
                  <>
                  <h5 className="fw-bold mb-4 mt-2">Log In to Continue</h5>
                    <div className="mb-3 form-group">
                      <div className="input-group">
                        <span className="input-group-text">+91</span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Phone Number"
                          value={loginPhone}
                          onChange={(e) => setLoginPhone(e.target.value)}
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-warning w-100 fw-bold"
                      disabled={loginLoading}
                      onClick={handleSendGuestOtp}
                    >
                      {loginLoading ? "Sending..." : "Generate OTP"}
                    </button>
                  </>
                ) : showOtpField ? (
                  <>
                    <p className="small mb-2">OTP sent to +91 {loginPhone}</p>
                    <div className="d-flex justify-content-center mb-3">
                      {otp.map((data, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          className="form-control text-center mx-1"
                          style={{ width: "45px" }}
                          value={data}
                          onChange={(e) => {
                            const newOtp = [...otp];
                            newOtp[index] = e.target.value;
                            setOtp(newOtp);
                            if (e.target.value && e.target.nextSibling) {
                              e.target.nextSibling.focus();
                            }
                          }}
                        />
                      ))}
                    </div>
                    <p className="small mb-4">
                      Didn't receive?{" "}
                      <a
                        href="#!"
                        className="text-warning fw-bold"
                        onClick={handleResendGuestOtp}
                      >
                        {resendingOtp ? "Resending..." : "Resend OTP"}
                      </a>
                    </p>
                    <button
                      className="btn btn-warning w-100 fw-bold"
                      disabled={loginLoading}
                      onClick={handleVerifyGuestOtp}
                    >
                      {loginLoading ? "Verifying..." : "Submit"}
                    </button>
                  </>
                ) : (
                  <>
                    <h5 className="fw-bold mb-3">Complete Registration</h5>
                    <div className="row mb-3">
                    <div className="col-md-6 text-start">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={profileData.first_name}
                        onChange={(e) =>
                          setProfileData((prev) => ({
                            ...prev,
                            first_name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-6 text-start">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={profileData.last_name}
                        onChange={(e) =>
                          setProfileData((prev) => ({
                            ...prev,
                            last_name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-12 text-start">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Optional"
                          value={profileData.email}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-12 text-start">
                        <div className="form-check d-flex align-items-center">
                          <input
                            type="checkbox"
                            id="login_is_adult_confirmed"
                            className="form-check-input"
                            style={{ width: "14px", height: "14px" }}
                            checked={profileData.is_adult_confirmed}
                            onChange={(e) =>
                              setProfileData((prev) => ({
                                ...prev,
                                is_adult_confirmed: e.target.checked,
                              }))
                            }
                          />
                          <label
                            htmlFor="login_is_adult_confirmed"
                            className="ms-2 mb-0"
                          >
                            I am 18+ years old
                          </label>
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-warning w-100 fw-bold"
                      disabled={loginLoading}
                      onClick={handleCompleteGuestRegistration}
                    >
                      {loginLoading ? "Submitting..." : "Complete Registration"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
